'use client'

import { Component, type ReactNode, useEffect, useMemo } from 'react'

import type { CanvasChatTab } from '@/components/canvas/use-canvas-chats'
import { api } from '@/convex/_generated/api'
import { useAuthedQuery } from '@/hooks/use-authed-query'

/**
 * Renders nothing; swallows any error from its subtree. Pruning the tab strip
 * is a non-critical self-heal, so a failure of the underlying query must never
 * crash the chat — it degrades to "no prune".
 */
class SilentBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch() {
    // Intentionally swallowed — see class doc.
  }

  render() {
    return this.state.failed ? null : this.props.children
  }
}

type PrunerProps = {
  orgId: string
  openTabs: ReadonlyArray<CanvasChatTab>
  onResolved: (brandMap: Record<string, string>) => void
}

// `brandId` isn't needed here — `chatBrandMap` is org-scoped, and the
// active-brand comparison happens in `pruneTabs`. We only gate the whole
// component on an active brand (in `ForeignTabPruner`) so it stays idle until
// a brand resolves.
function Pruner({ orgId, openTabs, onResolved }: PrunerProps) {
  // Only persisted tabs can be foreign; brand-new (`isNew`) placeholders have no
  // server-side brand yet and are always kept.
  const chatIds = useMemo(
    () => openTabs.filter((t) => !t.isNew).map((t) => t.chatId),
    [openTabs]
  )
  const brandMap = useAuthedQuery(
    api.chats.queries.chatBrandMap,
    chatIds.length > 0 ? { orgId, chatIds } : 'skip'
  )
  useEffect(() => {
    if (brandMap) {
      onResolved(brandMap)
    }
  }, [brandMap, onResolved])
  return null
}

/**
 * Headless child of `GlobalChatProvider`. Resolves the owning brand of every
 * open chat tab via `chatBrandMap` and feeds it to `pruneTabs`, which drops tabs
 * belonging to a sibling brand — the durable fix for cross-brand tab bleed.
 *
 * Wrapped in {@link SilentBoundary} so a `chatBrandMap` failure (e.g. a
 * deploy-skew window before the function is live, or a transient error) degrades
 * to "no prune" instead of throwing up to the chat's error boundary and taking
 * the whole chat surface down. No-ops without an active brand.
 */
export function ForeignTabPruner({
  orgId,
  brandId,
  openTabs,
  onResolved,
}: {
  orgId: string
  brandId: string | undefined
  openTabs: ReadonlyArray<CanvasChatTab>
  onResolved: (brandMap: Record<string, string>) => void
}) {
  if (!brandId) {
    return null
  }
  return (
    <SilentBoundary>
      <Pruner orgId={orgId} openTabs={openTabs} onResolved={onResolved} />
    </SilentBoundary>
  )
}
